import { Meta, Participant, SelectOption } from '@/types'
import ParticipantCard from './ui/ParticipantCard'
import { FiFilter } from 'react-icons/fi'
import {
  Accordion,
  AccordionPanel,
  AppPagination,
  ChallengeThemeSelection,
  Loading,
  MultipleFilter,
  Search,
} from '@/components/common'
import React, { useEffect, useState } from 'react'
import { fetchParticipants } from './action'
import { isEmpty, map } from 'lodash'
import { Event } from '@/types'
import { getSession } from 'next-auth/react'
import { useSearchParams } from 'next/navigation'
import { loadAllJobs, loadAllTech } from '@/utils/loadSingleColumn'
import { TEAM_STATUS } from '@/types/Participant'

const EmptyState = () => (
  <div className="flex flex-col w-full h-full items-center text-center">
    <span className="text-gray-900 text-lg font-semibold">
      Oops! There&apos;s no one here - yet 👀
    </span>
    <p className="max-w-md text-gray-500">
      There are no participants matching your criteria at the moment, but keep
      exploring or adjust your filters to broaden your search. The perfect team
      members are out there waiting for you!
    </p>
  </div>
)

const Participants = ({ event }: { event: Event }) => {
  const searchParams = useSearchParams()

  const [filterItems, setFilterItems] = useState<number>(0)
  const [hideFilter, setHideFilter] = useState<boolean>(true)
  const [loginEmail, setLoginEmail] = useState<string>('')
  const [loginUsername, setLoginUsername] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [participants, setParticipants] = useState<Participant[]>([])
  const [pagy, setPagy] = useState<Meta | undefined>(undefined)
  const [jobs, setJobs] = useState<{}>({})
  const [techStacks, setTechStacks] = useState<{}>({})

  useEffect(() => {
    const loadOptions = async () => {
      const jobs = await loadAllJobs()
      const techStacks = await loadAllTech()

      return {
        jobs: jobs,
        techStacks: techStacks,
      }
    }

    loadOptions()
      .then((res) => {
        setJobs(
          res?.jobs
            .map(({ label }: SelectOption) => label)
            .reduce((a: {}, v: string) => ({ ...a, [v]: v }), {})
        )
        setTechStacks(
          res?.techStacks
            .map(({ label }: SelectOption) => label)
            .reduce((a: {}, v: string) => ({ ...a, [v]: v }), {})
        )
      })
      .catch((error) => {
        console.error('Error fetching data:', error)
      })
  }, [])

  useEffect(() => {
    setLoading(true)

    fetchData().then((res) => {
      if (!isEmpty(res)) {
        setParticipants(res.data)
        setPagy(res.meta)
      }

      setLoading(false)
    })
  }, [searchParams])

  const fetchData = async () => {
    const session = await getSession()
    const keyword = searchParams.get('keyword') || ''
    const page = searchParams.get('page') || 1
    const challengeThemeId = searchParams.get('challengeThemeId') || ''
    const teamStatus = searchParams.get('teamStatus')?.split(',') || []
    const expertiseAreas = searchParams.get('expertiseAreas')?.split(',') || []
    const techStack = searchParams.get('techStack')?.split(',') || []

    setFilterItems(teamStatus.length + expertiseAreas.length + techStack.length)

    // Debug: Log session data
    // console.log('[Participants] fetchData debug', {
    //   session,
    //   user: session?.user,
    //   email: session?.user?.email,
    //   username: session?.user?.username,
    //   hasUser: !isEmpty(session?.user),
    // })

    // Set login info regardless of session validity
    setLoginEmail(session?.user?.email || '')
    setLoginUsername(session?.user?.username || '')

    if (session && !isEmpty(session?.user)) {
      return await fetchParticipants(
        event.slug,
        session?.user.id,
        keyword,
        Number(page),
        challengeThemeId,
        teamStatus,
        expertiseAreas,
        techStack
      )
    }
  }

  const handleClearFilter = () => {
    const params = new URLSearchParams(searchParams)
    params.delete('teamStatus')
    params.delete('expertiseAreas')
    params.delete('techStack')

    window.history.replaceState(null, '', `?${params.toString()}`)
  }

  return (
    <section>
      <div className="flex flex-col-reverse lg:flex-row items-stretch justify-between mb-6 gap-3">
        <div className="inline-flex gap-4">
          <div
            className="cursor-pointer"
            onClick={() => setHideFilter(!hideFilter)}
          >
            {hideFilter && filterItems === 0 ? (
              <div className="select-none px-5 py-2.5 rounded-lg border justify-center items-center gap-2 inline-flex cursor-pointer hover:bg-gray-800 hover:text-white transition-all">
                <FiFilter size={12} />
                <span className="text-sm font-medium">Filters</span>
              </div>
            ) : (
              <div className="select-none px-5 py-2.5 bg-gray-800 rounded-lg justify-center items-center gap-2 inline-flex">
                <FiFilter color="white" size={12} />
                <span className="text-white text-sm font-medium">Filters</span>
                {filterItems !== 0 && (
                  <div className="px-2.5 py-0.5 bg-gray-600 rounded-xl justify-center items-center flex">
                    <span className="text-center text-gray-100 text-xs">
                      {filterItems}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
          <ChallengeThemeSelection data={event.challenge_themes} />
        </div>
        <Search />
      </div>
      <div className="grid grid-cols-4 gap-6 relative">
        <div
          className={`${
            hideFilter && 'hidden'
          } border rounded-lg p-3 absolute lg:relative shadow top-0 left-0 z-10 bg-white`}
        >
          <div className="w-full inline-flex items-center justify-between mb-3">
            <span className="text-gray-500 text-sm font-medium">Filters</span>
            <div
              className="text-gray-500 text-sm font-medium cursor-pointer"
              onClick={() => handleClearFilter()}
            >
              Clear all
            </div>
          </div>
          <Accordion>
            <AccordionPanel title="Team Status">
              <MultipleFilter
                data={TEAM_STATUS}
                keyAsVal={true}
                filterType="teamStatus"
              />
            </AccordionPanel>
            <AccordionPanel title="Roles/Expertise Areas">
              <MultipleFilter data={jobs} filterType="expertiseAreas" />
            </AccordionPanel>
            <AccordionPanel title="Tech Stack">
              <MultipleFilter data={techStacks} filterType="techStack" />
            </AccordionPanel>
          </Accordion>
        </div>
        <div
          className={`${
            hideFilter ? 'lg:col-span-4' : 'lg:col-span-3'
          } col-span-4 relative`}
        >
          <div className="flex flex-col gap-6">
            <div className="text-gray-900 text-base leading-normal mb-4 font-semibold">
              {pagy?.count || 0} Participants
            </div>
            {participants.length > 0 && (
              <div
                className={`grid ${
                  hideFilter ? 'lg:grid-cols-3' : 'lg:grid-cols-2'
                } grid-cols-1 gap-6`}
              >
                {map(participants, (participant) => (
                  <ParticipantCard
                    key={participant.id}
                    participant={participant}
                    loginEmail={loginEmail}
                    loginUsername={loginUsername}
                    event={event}
                  />
                ))}
              </div>
            )}
            {participants.length === 0 && <EmptyState />}
            {pagy && (
              <div className="w-full mx-auto">
                <div className="flex flex-col lg:flex-row justify-between items-center overflow-hidden bg-white rounded dark:bg-gray-800 py-4">
                  <nav
                    className="flex flex-col items-start justify-between space-y-3 md:flex-row md:items-center md:space-y-0"
                    aria-label="Table navigation"
                  >
                    <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                      Showing{' '}
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {pagy.from}-{pagy.to}
                      </span>{' '}
                      of{' '}
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {pagy.count}
                      </span>
                    </span>
                  </nav>
                  {pagy.count > 1 && <AppPagination totalPages={pagy.pages} />}
                </div>
              </div>
            )}
          </div>

          {loading && <Loading />}
        </div>
      </div>
    </section>
  )
}

export default Participants
